import type React from "react";
import classNames from "classnames";
import { ShortcutEnterIcon, ShortcutShiftIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

type GlyphPair = [React.ReactNode, React.ReactNode];

const letterGlyphs: GlyphPair = ["G", "K"];

/** The two glyphs drawn for each key type: a sequence reads "first then second". */
const glyphsByVariant: Partial<Record<string, GlyphPair>> = {
    letter: letterGlyphs,
    text: ["Esc", "Ctrl"],
    symbol: ["⌘", "⌥"],
    equal: ["=", "="],
    icon: [
        <ShortcutShiftIcon key="shift" />,
        <ShortcutEnterIcon key="enter" />,
    ],
};

const KeyChip = ({
    read,
    highlight,
    glyph,
}: {
    read: ReturnType<typeof bindingReader>;
    highlight: AnatomyPartHighlight;
    glyph: React.ReactNode;
}) => (
    <span
        className={styles.schematic}
        style={{
            width: read("chip", "width"),
            minWidth: read("chip", "min-width"),
            height: read("chip", "height"),
        }}
    >
        <Part
            partId="chip"
            highlight={highlight}
            className={styles.buttonBorder}
            style={{
                background: read("chip", "background"),
                borderColor: read("chip", "border-color"),
                borderWidth: read("chip", "border-width"),
                borderRadius: read("chip", "border-radius"),
                boxShadow: read("chip", "box-shadow"),
            }}
        />
        <span
            className={styles.buttonContent}
            style={{
                width: "100%",
                justifyContent: "center",
                paddingInline: read("chip", "padding-inline"),
            }}
        >
            <Part
                partId="glyph"
                highlight={highlight}
                className={styles.buttonLabel}
                style={{
                    display: "inline-flex",
                    color: read("glyph", "color"),
                    fontFamily: read("glyph", "font-family"),
                    fontSize: read("glyph", "font-size"),
                    fontWeight: read("glyph", "font-weight"),
                    lineHeight: read("glyph", "line-height"),
                    letterSpacing: read("glyph", "letter-spacing"),
                }}
            >
                {glyph}
            </Part>
        </span>
    </span>
);

/** X-ray of a two-step Shortcut: chip, "then", chip, in the chosen key type. */
export const ShortcutSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);
    const [firstGlyph, secondGlyph] =
        glyphsByVariant[variantId] ?? letterGlyphs;

    return (
        <span
            className={classNames(styles.schematic, styles.buttonSchematic)}
            style={{ gap: read("separator", "gap") }}
        >
            <KeyChip read={read} highlight={highlight} glyph={firstGlyph} />
            <Part
                partId="separator"
                highlight={highlight}
                className={styles.buttonLabel}
                style={{
                    color: read("separator", "color"),
                    fontSize: read("separator", "font-size"),
                    letterSpacing: read("separator", "letter-spacing"),
                    lineHeight: 1,
                }}
            >
                then
            </Part>
            <KeyChip read={read} highlight={highlight} glyph={secondGlyph} />
        </span>
    );
};
